import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import * as echarts from "echarts/core";
import type { ECharts, EChartsCoreOption } from "echarts/core";
import { keepPreviousData } from "@tanstack/react-query";
import {
  useSkuDetailV1SkusPlatformProductIdGet,
  useSkuStoreDetailV1SkusPlatformProductIdStoresStoreIdGet,
  useStoreDetailV1StoresPlatformStoreIdGet,
} from "../api/generated/qcsControlPlane";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { EChart, cssVar } from "../components/EChart";
import {
  Badge,
  ErrorState,
  PageHeader,
  Panel,
  Skeleton,
  SkeletonRegion,
  Stack,
} from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg, Stat } from "../components/foundation";
import { type Theme, useTheme } from "../lib/theme-store";
import { AnimatedNumber } from "../lib/motion";
import { IconExternal } from "../components/icons";
import { WithRoutePlatform } from "../components/UnknownPlatform";
import { DataBody } from "../components/JsonPanel";
import { StoreDetailMap } from "../components/StoreDetailMap";
import { AnchorKindPill, TierPills } from "../components/tiers";
import type { SkuStorePoint } from "../api/generated/model";
import {
  type LatLon,
  type Platform,
  type StoreCell,
  ago,
  fmtDateTime,
  fmtInt,
  pdpUrl,
  platformLabel,
  storeStatusLabel,
  storeStatusTone,
} from "../api/labels";

// SKU x store detail page: one product at one dark store, the SKU's identity,
// the store's details + serviceable-area map, and the inventory-count-over-time
// chart for this exact pair. Reached by clicking a row in the SKU page's
// "Availability by store" table.
export function SkuStoreDetail() {
  const { platform = "", productId = "", storeId = "" } = useParams<{
    platform: string;
    productId: string;
    storeId: string;
  }>();
  return (
    <WithRoutePlatform platform={platform}>
      {(key) => <SkuStoreDetailView platform={key} productId={productId} storeId={storeId} />}
    </WithRoutePlatform>
  );
}

function SkuStoreDetailView({
  platform,
  productId,
  storeId,
}: {
  platform: Platform;
  productId: string;
  storeId: string;
}) {
  const [params] = useSearchParams();
  const from = params.get("from");

  // One window for the whole page: the charts, the estimated sales and the observations
  // table all read the same request, so they can never disagree about what they cover.
  // Same three options as the SKU page, so moving between the two keeps its frame.
  const [days, setDays] = useState<"7" | "14" | "30">("14");

  const q = useSkuStoreDetailV1SkusPlatformProductIdStoresStoreIdGet(
    platform,
    encodeURIComponent(productId),
    encodeURIComponent(storeId),
    { days: Number(days) },
    // Changing the window refires the one request the whole page reads, so without the
    // previous data the header, maps and tables all fall back to skeletons for the round
    // trip. Only the two charts say they are reloading, by dimming.
    { query: { retry: false, placeholderData: keepPreviousData } },
  );
  const storeQ = useStoreDetailV1StoresPlatformStoreIdGet(platform, encodeURIComponent(storeId), {
    query: { retry: false },
  });
  // SKU-level context (status, lifecycle, price range across all stores) so the
  // page carries the same SKU detail as the standalone SKU page and you don't
  // have to open it to see them. Best-effort: the pair still renders without it.
  const skuQ = useSkuDetailV1SkusPlatformProductIdGet(platform, encodeURIComponent(productId), {
    query: { retry: false },
  });

  const d = q.data;
  const store = storeQ.data;
  const skuInfo = skuQ.data;
  const sku = d?.sku;

  useBreadcrumbTail(
    sku
      ? [
          ...(from ? [{ label: sku.brand || "brand", to: `/brands/${from}` }] : []),
          {
            label: sku.name || productId,
            to: `/skus/${platform}/${encodeURIComponent(productId)}${from ? `?from=${from}` : ""}`,
          },
          { label: store?.name || storeId },
        ]
      : [{ label: storeId }],
  );

  const head = (
    <PageHeader
      className="mb-3.5"
      title={sku?.name || productId}
      titleMeta={
        <>
          <span className={`plat ${platform}`} style={{ fontSize: 13 }}>{platformLabel(platform)}</span>
          <span className="faint" style={{ fontSize: 13 }}>@ {store?.name || storeId}</span>
          {sku && (
            <a
              href={pdpUrl(platform, sku.product_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="as-link"
              title="Open product page on the storefront"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12.5,
              }}
            >
              <span style={{ fontSize: 12, display: "inline-flex" }}><IconExternal /></span>
              Storefront
            </a>
          )}
        </>
      }
      description={
        <>
          {platformLabel(platform)} · this SKU at this dark store
          {sku?.pack_size ? ` · ${sku.pack_size}` : ""}
        </>
      }
    />
  );

  if (q.isError) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load this SKU at this store"
              message={
                <>
                  Store <span className="mono">{storeId}</span> may not be in the current{" "}
                  {platformLabel(platform)} dimension.
                </>
              }
              onRetry={() => q.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }
  if (!d || !sku) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body tight>
            <SkeletonRegion>
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      </>
    );
  }

  // The pair's newest observation whatever the window (the backend returns it beside the
  // windowed history). Narrowing to 7d on a pair last swept a fortnight ago must not blank
  // the header: these tiles answer "where does this pair stand now", not "in the window".
  const latest = d.latest ?? d.history[d.history.length - 1]; // history is oldest-first
  // Latest cross-store price/availability roll-up for this SKU (price_history is
  // newest-first). Powers the "all stores" context rows in the SKU panel.
  const skuLatest = skuInfo?.price_history?.[0];
  const salesDays = pairSales(d.history);
  // Table shows newest-first by default (matches the old reversed render); the
  // user can re-sort any column from there.
  const observations = [...d.history].reverse();

  return (
    <>
      {head}

      {/* at-a-glance KPIs from the most recent observation */}
      <div className="grid g-4" style={{ marginBottom: 14 }}>
        <Stat
          surface
          label="latest inventory"
          value={
            latest?.inventory == null ? (
              "—"
            ) : (
              <>
                {isFloor(latest) && <span className="faint" style={{ fontSize: 13 }}>≥ </span>}
                <AnimatedNumber value={latest.inventory} format={fmtInt} />
              </>
            )
          }
          tone={latest && latest.inventory === 0 ? "warn" : undefined}
          foot={latest ? basisNote(latest.quantity_basis) : undefined}
        />
        <Stat
          surface
          label="latest price"
          value={
            latest && latest.selling_price != null && latest.selling_price > 0 ? (
              <AnimatedNumber value={latest.selling_price} format={money} />
            ) : (
              money(latest?.selling_price)
            )
          }
        />
        <Stat
          surface
          label="stock"
          value={
            latest?.is_out_of_stock == null ? "—" : latest.is_out_of_stock ? "Out of stock" : "In stock"
          }
          tone={latest?.is_out_of_stock ? "warn" : latest?.is_out_of_stock === false ? "ok" : undefined}
        />
        <Stat
          surface
          label={`observations (${days}d)`}
          value={<AnimatedNumber value={d.history.length} format={fmtInt} />}
        />
      </div>

      {/* Price above, stock count below, on one x axis: whether a price move shifted stock
          is the question this page gets asked, and stacking the two makes that eyeball
          work. Two ECharts instances (one per panel) kept in step by PairCharts. */}
      <PairCharts
        history={d.history}
        days={days}
        onDays={setDays}
        everObserved={d.latest != null}
        fetching={q.isFetching}
      />

      {/* estimated sales at THIS store, folded client-side from the same history the
          chart above already has -- no extra request */}
      {salesDays.length > 0 && (
        <Panel className="mb-3.5">
          <Panel.Head title="Sales over time (estimated)">
            <div className="flex-1" />
            <span className="faint" style={{ fontSize: 11 }}>last {days} days</span>
          </Panel.Head>
          <Panel.Body tight>
            <PairSalesChart days={salesDays} />
            <span className="hint" style={{ marginTop: 6 }}>
              Falls in this store's reported quantity between consecutive sweeps, valued at the
              observed selling price. Only consecutive observations count, so a restock inside a
              gap hides the sales before it: this <strong>under-counts</strong>.
            </span>
          </Panel.Body>
        </Panel>
      )}

      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(12,1fr)",
          marginBottom: 14,
          alignItems: "start",
        }}
      >
        {/* SKU details */}
        <Panel style={{ gridColumn: "span 5" }}>
          <Panel.Head title="SKU" />
          <Panel.Body tight>
            <a
              href={pdpUrl(platform, sku.product_id)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open product page on the storefront"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 140,
                margin: "2px 0 12px",
                borderRadius: 8,
                border: "1px solid var(--line)",
                background: "var(--img-plate)",
                overflow: "hidden",
              }}
            >
              {sku.image_url ? (
                <img
                  src={sku.image_url}
                  alt={sku.name}
                  loading="lazy"
                  style={{
                    maxHeight: 132,
                    maxWidth: "92%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <span className="faint" style={{ fontSize: 12 }}>no image captured</span>
              )}
            </a>
            <table className="tbl">
              <tbody>
                <Row k="Name" v={
                  <Link to={`/skus/${platform}/${encodeURIComponent(productId)}${from ? `?from=${from}` : ""}`} style={{ color: "inherit" }}>
                    {sku.name || "—"}
                  </Link>
                } />
                <Row k="Product ID" v={<span className="mono">{sku.product_id}</span>} />
                <Row k="Variant ID" v={sku.variant_id ? <span className="mono">{sku.variant_id}</span> : dash} />
                <Row k="SKU code" v={sku.sku ? <span className="mono">{sku.sku}</span> : dash} />
                <Row k="Brand" v={sku.brand || dash} />
                <Row k="Manufacturer" v={sku.manufacturer || dash} />
                <Row k="Pack size" v={sku.pack_size || dash} />
                <Row k="Category" v={(sku.category_path ?? []).join(" › ") || dash} />
                <Row k="Product type" v={sku.product_type || dash} />
                <Row k="Status" v={sku.status ? <Badge variant="outline" style={{ fontSize: 10 }}>{sku.status}</Badge> : dash} />
                {skuLatest && (
                  <>
                    <Row
                      k="Price (all stores)"
                      v={priceRange(skuLatest.sell_min, skuLatest.sell_max)}
                    />
                    <Row
                      k="MRP (all stores)"
                      v={priceRange(skuLatest.mrp_min, skuLatest.mrp_max)}
                    />
                    <Row
                      k="Availability"
                      v={
                        <span>
                          {fmtInt(skuLatest.in_stock)} in stock
                          <span className="faint"> / {fmtInt(skuLatest.stores)} stores</span>
                        </span>
                      }
                    />
                  </>
                )}
                {skuInfo && (
                  <>
                    <Row k="First seen" v={skuInfo.first_seen_at ? `${fmtDateTime(skuInfo.first_seen_at)} · ${ago(skuInfo.first_seen_at)}` : dash} />
                    <Row k="Last seen" v={skuInfo.last_seen_at ? `${fmtDateTime(skuInfo.last_seen_at)} · ${ago(skuInfo.last_seen_at)}` : dash} />
                  </>
                )}
              </tbody>
            </table>
          </Panel.Body>
        </Panel>

        {/* store details */}
        <Panel style={{ gridColumn: "span 7" }}>
          <Panel.Head title="Store">
            {store && (
              <Badge variant={toneVariant(storeStatusTone(store.status))} style={{ marginLeft: 8, fontSize: 10.5 }}>
                {storeStatusLabel(store.status)}
              </Badge>
            )}
            <div className="flex-1" />
            <Link to={`/stores/${platform}/${encodeURIComponent(storeId)}`} className="as-link" style={{ fontSize: 12 }}>
              Full store page →
            </Link>
          </Panel.Head>
          <Panel.Body tight>
            {storeQ.isLoading && <div className="muted" style={{ padding: 12 }}>Loading store…</div>}
            {store && (
              <>
                {store.boundary || store.cells.length > 0 ? (
                  <div style={{ marginBottom: 10 }}>
                    <StoreDetailMap
                      platform={platform}
                      anchor={[store.latitude, store.longitude]}
                      boundary={store.boundary as LatLon[] | null}
                      cells={store.cells as StoreCell[]}
                    />
                  </div>
                ) : null}
                <table className="tbl">
                  <tbody>
                    <Row k="Store ID" v={<span className="mono">{store.store_id}</span>} />
                    <Row k="Name" v={store.name || dash} />
                    <Row k="Tier" v={<TierPills tiers={store.tiers} fallback={store.tier} />} />
                    <Row k="ETA" v={store.eta_minutes == null ? dash : `${store.eta_minutes} min`} />
                    <Row k="Location" v={[store.locality, store.city, store.state].filter(Boolean).join(", ") || dash} />
                    <Row k="Address" v={store.formatted_address || store.address || dash} />
                    <Row k="Pincode" v={store.pincode ? <span className="mono">{store.pincode}</span> : dash} />
                    <Row k="Coordinates" v={<span className="mono">{store.latitude.toFixed(6)}, {store.longitude.toFixed(6)}</span>} />
                    <Row k="Serviceability" v={store.serviceability_type || dash} />
                    <Row k="Order types" v={store.order_types.length ? store.order_types.join(", ") : dash} />
                    <Row k="Primary order type" v={store.primary_order_type || dash} />
                    <Row k="Nightly store" v={store.is_nightly_store == null ? dash : store.is_nightly_store ? "Yes" : "No"} />
                    <Row k="Cost" v={store.cost == null ? dash : `₹${store.cost}`} />
                    <Row k="Fulfilment cost" v={store.fulfilment_cost == null ? dash : `₹${store.fulfilment_cost}`} />
                    <Row k="Anchor kind" v={<AnchorKindPill kind={store.anchor_kind} />} />
                    <Row k="Geocode source" v={store.geo_source || dash} />
                    {store.always_shadowed ? (
                      <Row k="Always shadowed" v={<Badge variant="outline-warning">Yes - never wins routing (cascade-only)</Badge>} />
                    ) : null}
                    <Row k="First seen" v={store.first_seen_at ? `${fmtDateTime(store.first_seen_at)} · ${ago(store.first_seen_at)}` : dash} />
                    <Row k="Last seen" v={store.last_seen_at ? `${fmtDateTime(store.last_seen_at)} · ${ago(store.last_seen_at)}` : dash} />
                  </tbody>
                </table>
                {/* platform-reported detail (ETA breakdown / serviceability extras /
                    geofence) as a sub-section instead of three loose JSON cards the
                    reader had to scroll past to reach the observations table */}
                {(store.eta_map || store.geofence || store.extra) && (
                  <>
                    <div className="panel-sub">Platform data</div>
                {/* one scroll frame for the group: these blobs are unbounded (a store with
                    nine ETA tiers runs a full screen) and would otherwise stretch this
                    column far past the map beside it */}
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                    {store.eta_map && (
                      <>
                        <div className="faint" style={{ fontSize: 11, margin: "6px 0 4px" }}>ETA map</div>
                        <DataBody raw={store.eta_map} />
                      </>
                    )}
                    {store.extra && (
                      <>
                        <div className="faint" style={{ fontSize: 11, margin: "10px 0 4px" }}>Extra</div>
                        <DataBody raw={store.extra} />
                      </>
                    )}
                    {store.geofence && (
                      <>
                        <div className="faint" style={{ fontSize: 11, margin: "10px 0 4px" }}>Geofence</div>
                        <DataBody raw={store.geofence} />
                      </>
                    )}
                    </div>
                  </>
                )}
              </>
            )}
          </Panel.Body>
        </Panel>
      </div>

      {/* raw observations */}
      <Panel>
        <Panel.Head title="Observations">
          <div className="flex-1" />
          <span className="num faint" style={{ fontSize: 11 }}>{d.history.length} sweeps</span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="sku-store-obs"
            columns={OBS_COLUMNS}
            data={observations}
            getRowId={(h, i) => `${h.run_id}-${h.fetched_at}-${i}`}
            globalPlaceholder="Search runs…"
            csvFilename={`${platform}-${productId}-${storeId}-observations`}
            emptyMessage="No observations."
            isLoading={q.isLoading}
          />
        </Panel.Body>
      </Panel>
    </>
  );
}

// -- inventory / price chart ---------------------------------------------------
/** Whether any sweep of this pair read a stock count, which decides the chart's title. */
const hasInventory = (history: SkuStorePoint[]): boolean =>
  history.some((h) => h.inventory != null);

// Quantity provenance determines both its display note and whether sales can use it.
const BASIS_NOTE: Record<string, string> = {
  assumed_out_of_stock: "Assumed out of stock from an empty cart; stock was not measured.",
  purchase_cap: "at least this many, the per-order purchase cap answered, not the shelf",
  ask_ceiling: "at least this many, the cart gave us everything we asked for",
  in_stock_floor: "in stock, amount not measured, only a catalogue flag was read",
};

// The field is optional in the generated client (rows written before the label existed omit
// it), and an absent basis is exactly the "no claim about this number" case: not a floor.
const FLOOR_BASES = new Set(["purchase_cap", "ask_ceiling", "in_stock_floor"]);
const EXACT_BASES = new Set(["", "measured", "out_of_stock"]);
const isFloor = (point: SkuStorePoint): boolean =>
  FLOOR_BASES.has(point.quantity_basis ?? "");
export const basisNote = (basis: string | undefined): string | undefined =>
  Object.hasOwn(BASIS_NOTE, basis ?? "") ? BASIS_NOTE[basis ?? ""] : undefined;

/** One sweep's quantity as text, prefixed when it is only a floor; null when nothing was read. */
export const quantityText = (point: SkuStorePoint): string | null =>
  point.inventory == null
    ? null
    : isFloor(point)
      ? `≥ ${fmtInt(point.inventory)}`
      : fmtInt(point.inventory);

// Per-day estimated sales for ONE (store, SKU) pair: the same fold the SKU page's
// server-side query does, but over the history array already on this page.
// `Math.max(0, prev - cur)` is domain logic, not a clamp -- a rise is a restock.
type PairSalesDay = { day: string; units: number; value: number };

export function pairSales(history: SkuStorePoint[]): PairSalesDay[] {
  const byDay = new Map<string, PairSalesDay>();
  let prev: number | null = null;
  for (const h of history) {
    // history is oldest-first; a NULL-quantity sweep is unknown, not zero, so it breaks
    // the chain rather than reading as a sell-out. Only exact quantities can extend it;
    // policy zeros, floors and unknown bases are not measured stock changes.
    if (h.inventory == null || !EXACT_BASES.has(h.quantity_basis ?? "")) {
      prev = null;
      continue;
    }
    if (prev != null && prev > h.inventory) {
      const units = prev - h.inventory;
      const day = istDay(h.fetched_at);
      const cur = byDay.get(day) ?? { day, units: 0, value: 0 };
      cur.units += units;
      cur.value += units * (h.selling_price ?? 0);
      byDay.set(day, cur);
    }
    prev = h.inventory;
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

// Bucket by the IST day, matching the server-side sales derivation.
function istDay(ts: string): string {
  const d = new Date(ts.endsWith("Z") || ts.includes("+") ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime())
    ? ts.slice(0, 10)
    : new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(d);
}

function PairSalesChart({ days }: { days: PairSalesDay[] }) {
  const { theme } = useTheme();
  const option = useMemo<EChartsCoreOption>(() => pairSalesOption(days, theme), [days, theme]);
  return <EChart option={option} height={170} />;
}

function pairSalesOption(points: PairSalesDay[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const brand = cssVar("--brand", "#e0a325");
  const info = cssVar("--info", "#2f6feb");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  return {
    grid: { left: 8, right: 34, top: 26, bottom: 6, containLabel: true },
    legend: {
      show: true,
      top: 0,
      right: 4,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: { color: faint, fontSize: 10, fontFamily: mono },
    },
    tooltip: {
      trigger: "axis",
      // the panel clips its overflow, so a tall tooltip near the top gets its head cut
      // off unless it is rendered on <body> instead of inside the chart container
      appendToBody: true,
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
      formatter: (raw: unknown) => {
        const arr = raw as { dataIndex: number }[];
        const p = points[arr[0]?.dataIndex];
        if (!p) return "";
        return [
          `<b>${fmtInt(p.units)}</b> units sold`,
          `<span style="opacity:.7">₹${p.value.toFixed(0)} estimated value</span>`,
          `<span style="opacity:.7">${p.day}</span>`,
        ].join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      data: points.map((p) => p.day.slice(5)),
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
    },
    yAxis: [
      // no axis names: the legend already names both series in their own colour, and a
      // right-hand name sits exactly where the legend draws
      {
        type: "value",
        axisLabel: { color: brand, fontSize: 9, fontFamily: mono },
        splitLine: { lineStyle: { color: line, type: "dashed" } },
      },
      {
        type: "value",
        axisLabel: {
          color: info,
          fontSize: 9,
          fontFamily: mono,
          formatter: (v: number) => `₹${v}`,
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "Units",
        type: "bar",
        yAxisIndex: 0,
        data: points.map((p) => p.units),
        itemStyle: { color: brand, opacity: 0.75 },
        barMaxWidth: 22,
      },
      {
        name: "Value (INR)",
        type: "line",
        yAxisIndex: 1,
        data: points.map((p) => p.value),
        showSymbol: true,
        symbolSize: 5,
        lineStyle: { color: info, width: 1.75 },
        itemStyle: { color: info },
      },
    ],
  };
}

// IST is +05:30 and the charts are read by people in it, so the axis is shifted into IST
// and drawn with `useUTC`. Without that ECharts places its day and hour ticks on the
// BROWSER's midnight, and a laptop left on UTC labels the day boundary 05:30.
const IST_OFFSET_MS = 5.5 * 3600 * 1000;

/** An observation's timestamp as the epoch ms the time axis plots it at. */
function axisMs(ts: string): number {
  const d = new Date(ts.endsWith("Z") || ts.includes("+") ? ts : `${ts}Z`);
  return d.getTime() + IST_OFFSET_MS;
}

// Above this many sweeps a symbol per point draws as a solid band rather than as points,
// so the line carries the shape on its own. 30 days of an hourly pair is around 500.
const SYMBOL_LIMIT = 200;

/** The x axis both charts share. Only the lower chart draws its labels. */
function sharedTimeAxis(labels: boolean, line: string, faint: string, mono: string) {
  return {
    type: "time" as const,
    // Real clock positions, not one step per sweep: a six-hour hole between two readings
    // has to draw as a six-hour hole, or a scrape outage reads as an uneventful flat line.
    axisLine: { lineStyle: { color: line } },
    axisTick: { show: false },
    axisLabel: {
      show: labels,
      color: faint,
      fontSize: 9,
      fontFamily: mono,
      hideOverlap: true,
      margin: 8,
    },
    splitLine: { show: false },
  };
}

// Both charts sit in their own panel, so ECharts cannot lay their plot areas out together.
// `containLabel: false` plus this fixed left gutter is what keeps a given moment at the
// same pixel in both: let ECharts size the gutter and the price chart's wider label
// ("\u20b91,299" against "12") shifts its axis and the two stop lining up.
const AXIS_GUTTER = 46;

/** Everything both charts share: theme colours, the x axis, the tooltip lookup. */
function chartBase(points: SkuStorePoint[]) {
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  return {
    ink2: cssVar("--ink-2", "#444"),
    faint,
    line,
    mono,
    brand: cssVar("--brand", "#e0a325"),
    info: cssVar("--info", "#2f6feb"),
    bg: cssVar("--panel", "#fff"),
    bad: cssVar("--crit", "#e5484d"),
    // The tooltip is triggered on the axis, and the two charts hold different series
    // (and, for the stock-out overlay, different point counts), so a shared lookup by
    // timestamp is the only index that means the same thing in both.
    byTime: new Map(points.map((p) => [axisMs(p.fetched_at), p])),
    grid: { left: AXIS_GUTTER, right: 12, containLabel: false },
    axis: (labels: boolean) => sharedTimeAxis(labels, line, faint, mono),
  };
}

/** The tooltip shared by both charts: one observation, whichever chart you hover. */
function pairTooltip(
  base: ReturnType<typeof chartBase>,
  withCounts: boolean,
): Record<string, unknown> {
  return {
    trigger: "axis",
    // the panel clips its overflow, so a tall tooltip near the top gets its head cut
    // off unless it is rendered on <body> instead of inside the chart container
    appendToBody: true,
    backgroundColor: base.bg,
    borderColor: base.line,
    borderWidth: 1,
    padding: [6, 9],
    textStyle: { color: base.ink2, fontSize: 11, fontFamily: base.mono },
    formatter: (raw: unknown) => {
      const arr = raw as { axisValue: number }[];
      const p = base.byTime.get(arr[0]?.axisValue);
      if (!p) return "";
      const stock = p.is_out_of_stock == null ? "" : p.is_out_of_stock ? " · OOS" : " · in stock";
      const note = basisNote(p.quantity_basis);
      return [
        // With no count anywhere in the window there is nothing to lead with, so the
        // stock verdict stands on its own rather than dressing an em dash as a number.
        withCounts ? `<b>${quantityText(p) ?? "\u2014"}</b> in stock${stock}` : stock.slice(3),
        note ? `<span style="opacity:.7">${note}</span>` : "",
        `<span style="opacity:.7">selling ${money(p.selling_price)} \u00b7 MRP ${money(p.mrp)}</span>`,
        `<span style="opacity:.7">${fmtDateTime(p.fetched_at)}</span>`,
      ].filter(Boolean).join("<br/>");
    },
  };
}

/**
 * @param withCounts whether the inventory chart is drawn below this one. It owns the x
 *   axis labels when it is; with no stock count it is replaced by a message, and the
 *   price chart has to label its own axis or the window has no dates on it at all.
 */
function priceOption(
  points: SkuStorePoint[],
  withCounts: boolean,
  _theme: Theme,
): EChartsCoreOption {
  const base = chartBase(points);
  // Nothing here interpolates. A price is a stated value that holds until the merchant
  // restates it, so a straight line between two sweeps would draw numbers that never
  // existed; the step draws the price we were actually quoted the whole time.
  const series = (
    name: string,
    pick: (p: SkuStorePoint) => number | null | undefined,
    color: string,
    dashed: boolean,
  ) => ({
    name,
    type: "line" as const,
    data: points.map((p) => [axisMs(p.fetched_at), pick(p) ?? null]),
    connectNulls: false, // a nulled price is a sweep we learned no price at, not a zero
    showSymbol: false,
    step: "end" as const,
    lineStyle: { color, width: 1.5, type: dashed ? ("dashed" as const) : ("solid" as const) },
    itemStyle: { color },
    emphasis: { disabled: true },
  });
  return {
    useUTC: true,
    grid: { ...base.grid, top: 26, bottom: withCounts ? 12 : 20 },
    legend: {
      show: true,
      top: 0,
      right: 4,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: { color: base.faint, fontSize: 10, fontFamily: base.mono },
    },
    tooltip: pairTooltip(base, withCounts),
    axisPointer: { link: [{ xAxisIndex: "all" }] },
    xAxis: base.axis(!withCounts),
    // ALWAYS from zero: a list price that moves 390 -> 399 is a 2% change, and on a
    // fitted axis it draws as a cliff that dwarfs the discount underneath it.
    yAxis: {
      type: "value",
      min: 0,
      splitNumber: 3,
      axisLabel: {
        color: base.info,
        fontSize: 9,
        fontFamily: base.mono,
        formatter: (v: number) => `\u20b9${v}`,
      },
      splitLine: { show: true, lineStyle: { color: base.line, type: "dashed" } },
    },
    series: [
      series("Selling", (p) => p.selling_price, base.info, false),
      series("MRP", (p) => p.mrp, base.faint, true),
    ],
  };
}

function inventoryOption(points: SkuStorePoint[], _theme: Theme): EChartsCoreOption {
  const base = chartBase(points);
  // Below the symbol limit every sweep gets its own dot; above it they merge into a band
  // and the line's shape says more than the dots do.
  const symbols = points.length <= SYMBOL_LIMIT;
  return {
    useUTC: true,
    grid: { ...base.grid, top: 26, bottom: 20 },
    legend: {
      show: true,
      top: 0,
      right: 4,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: { color: base.faint, fontSize: 10, fontFamily: base.mono },
    },
    tooltip: pairTooltip(base, true),
    axisPointer: { link: [{ xAxisIndex: "all" }] },
    xAxis: base.axis(true),
    yAxis: {
      type: "value",
      scale: true,
      splitNumber: 3,
      // A shelf holds whole units, so a gridline at 0.6 describes nothing. Pairs on these
      // platforms often move between 0 and 1, where the default split would otherwise
      // label four fractional ticks nobody can act on.
      minInterval: 1,
      axisLabel: { color: base.brand, fontSize: 9, fontFamily: base.mono },
      splitLine: { show: true, lineStyle: { color: base.line, type: "dashed" } },
    },
    series: [
      {
        name: "Stock count",
        type: "line" as const,
        data: points.map((p) => [axisMs(p.fetched_at), p.inventory ?? null]),
        connectNulls: false, // a null count is a sweep we read no count in, not a zero
        showSymbol: symbols,
        symbolSize: 6,
        step: "end" as const,
        lineStyle: { color: base.brand, width: 1.75 },
        itemStyle: { color: base.brand, borderColor: base.bg, borderWidth: 1 },
        areaStyle: { color: base.brand, opacity: 0.08 },
        emphasis: { scale: 1.5 },
      },
      // Stock-outs used to be one red dot among the line's own symbols, which disappears
      // at 30 days once symbols are off. As their own series they stay visible at every
      // window, and a stock-out is a property of the reading anyway, not of the line.
      {
        name: "Out of stock",
        type: "line" as const,
        data: points.map((p) => [axisMs(p.fetched_at), p.is_out_of_stock ? (p.inventory ?? 0) : null]),
        showSymbol: true,
        symbolSize: 5,
        lineStyle: { opacity: 0 },
        itemStyle: { color: base.bad },
        tooltip: { show: false },
      },
    ],
  };
}

/**
 * The price and stock-count charts as two stacked panels on one x axis.
 *
 * ECharts gives a shared axis away inside ONE instance; across two panels it has to be
 * built: identical time axes over the same points, the fixed `AXIS_GUTTER` so both plot
 * areas start at the same pixel, and `echarts.connect` so hovering either draws the
 * crosshair on both.
 */
function PairCharts({
  history,
  days,
  onDays,
  everObserved,
  fetching,
}: {
  history: SkuStorePoint[];
  days: "7" | "14" | "30";
  onDays: (v: "7" | "14" | "30") => void;
  everObserved: boolean;
  fetching: boolean;
}) {
  const { theme } = useTheme();
  const priceRef = useRef<ECharts | null>(null);
  const invRef = useRef<ECharts | null>(null);
  const withCounts = hasInventory(history);
  const price = useMemo(
    () => priceOption(history, withCounts, theme),
    [history, withCounts, theme],
  );
  const inventory = useMemo(() => inventoryOption(history, theme), [history, theme]);

  // connect() groups the two instances so an axisPointer in one is echoed in the other.
  // Both must exist first, and the option swap on a window change disposes neither, so
  // this runs once the pair is mounted and is undone when either goes away.
  const group = useId();
  useEffect(() => {
    const a = priceRef.current;
    const b = invRef.current;
    if (!a || !b) return;
    // Connect by group id, not by instance: disconnect() only takes a group, so a pair
    // joined as bare instances could never be taken apart again and the next mount would
    // echo its axisPointer into the disposed one.
    a.group = group;
    b.group = group;
    echarts.connect(group);
    return () => echarts.disconnect(group);
  }, [group, withCounts]);

  const dim = fetching ? { opacity: 0.6, transition: "opacity .15s" } : undefined;
  const windowControl = (
    <>
      <div className="flex-1" />
      <Seg
        value={days}
        options={[
          { value: "7", label: "7d" },
          { value: "14", label: "14d" },
          { value: "30", label: "30d" },
        ]}
        onChange={onDays}
      />
    </>
  );

  if (history.length === 0) {
    return (
      <Panel className="mb-3.5">
        <Panel.Head title="Price over time">{windowControl}</Panel.Head>
        <Panel.Body tight>
          <div className="muted" style={{ padding: 16, textAlign: "center" }}>
            {everObserved
              ? `No observations of this SKU at this store in the last ${days} days.`
              : "No observations of this SKU at this store yet."}
          </div>
        </Panel.Body>
      </Panel>
    );
  }

  return (
    <>
      <Panel className="mb-2">
        <Panel.Head title="Price over time">{windowControl}</Panel.Head>
        <Panel.Body tight>
          <div style={dim}>
            <EChart option={price} height={150} instanceRef={priceRef} />
          </div>
        </Panel.Body>
      </Panel>
      <Panel className="mb-3.5">
        <Panel.Head title="Inventory over time" />
        <Panel.Body tight>
          {withCounts ? (
            <div style={dim}>
              <EChart option={inventory} height={130} instanceRef={invRef} />
            </div>
          ) : (
            <div className="muted" style={{ padding: 16, textAlign: "center" }}>
              No stock count reported for this SKU at this store, the cart answered whether
              it is buyable without ever stating how many, so only price is drawn.
            </div>
          )}
        </Panel.Body>
      </Panel>
    </>
  );
}

// -- observations table --------------------------------------------------------
/** One sweep's quantity for the observations table: the text, or a dash element. */
function quantityLabel(point: SkuStorePoint): ReactNode {
  return quantityText(point) ?? dash;
}

const quantityNote = (point: SkuStorePoint): string | undefined => basisNote(point.quantity_basis);

// Raw per-sweep observations of this SKU at this store. Numeric columns sort by
// their underlying number (rendered formatted); Stock is a faceted filter.
const OBS_COLUMNS: AnyColumnDef<SkuStorePoint>[] = (() => {
  const oc = createColumnHelper<SkuStorePoint>();
  return [
    oc.accessor("fetched_at", {
      id: "when",
      header: "When",
      cell: ({ row }) => <span className="faint" style={{ fontSize: 11.5 }}>{fmtDateTime(row.original.fetched_at)}</span>,
      meta: { minWidth: 180, exportValue: (h) => h.fetched_at },
    }),
    oc.accessor((h) => h.inventory, {
      id: "inventory",
      header: "Inventory",
      cell: ({ row }) => (
        <span style={{ fontSize: 11.5 }} title={quantityNote(row.original)}>
          {quantityLabel(row.original)}
        </span>
      ),
      meta: { align: "right", cellClassName: "num", width: 120 },
    }),
    oc.accessor((h) => h.selling_price, {
      id: "selling",
      header: "Selling",
      cell: ({ row }) => <span style={{ fontSize: 11.5 }}>{money(row.original.selling_price)}</span>,
      meta: { align: "right", cellClassName: "num", width: 110 },
    }),
    oc.accessor((h) => h.mrp, {
      id: "mrp",
      header: "MRP",
      cell: ({ row }) => <span className="faint" style={{ fontSize: 11.5 }}>{money(row.original.mrp)}</span>,
      meta: { align: "right", cellClassName: "num", width: 110 },
    }),
    oc.accessor((h) => (h.is_out_of_stock == null ? "" : h.is_out_of_stock ? "OOS" : "in stock"), {
      id: "stock",
      header: "Stock",
      cell: ({ row }) =>
        row.original.is_out_of_stock == null ? (
          dash
        ) : (
          <Badge variant={toneVariant(row.original.is_out_of_stock ? "warn" : "ok")} style={{ fontSize: 10 }}>
            {row.original.is_out_of_stock ? "OOS" : "in stock"}
          </Badge>
        ),
      meta: {
        align: "right",
        width: 120,
        filterSelect: true,
        filterLabel: "Stock",
      },
    }),
    oc.accessor("run_id", {
      id: "run",
      header: "Run",
      cell: ({ row }) => (
        <Link className="mono" to={`/runs/${row.original.run_id}`} style={{ fontSize: 11 }}>
          {row.original.run_id.slice(0, 8)}
        </Link>
      ),
      meta: { minWidth: 130 },
    }),
  ];
})();

// -- small helpers -------------------------------------------------------------
const dash = <span className="faint">—</span>;

function money(n: number | null | undefined): string {
  if (n == null || n <= 0) return "—";
  return `₹${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)}`;
}

// A min-max money range, collapsing to a single value when they match.
function priceRange(min: number | null | undefined, max: number | null | undefined): ReactNode {
  const lo = money(min);
  const hi = money(max);
  if (lo === "—" && hi === "—") return dash;
  if (lo === hi || hi === "—") return lo;
  if (lo === "—") return hi;
  return `${lo} - ${hi}`;
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <tr>
      <td className="faint" style={{ whiteSpace: "nowrap", width: 1, paddingRight: 18 }}>{k}</td>
      <td className="strong" style={{ fontSize: 12.5 }}>{v}</td>
    </tr>
  );
}
